import { getApiUrl, isApiDisabled } from '../city-config.js';
import { LIVE_DATA_MAX_AGE_MS } from './fetchArrivals.js';

const recentRequests = new Map();
const REQUEST_DEDUPE_MS = 2000;

const IDLE_STATUS = { loading: false, error: false, source: null };

/**
 * Return true if a vehicle has lastRefreshMs and it is older than LIVE_DATA_MAX_AGE_MS (15 min).
 * @param {Object} vehicle - Vehicle object (may have lastRefreshMs)
 * @returns {boolean}
 */
export function isVehicleStale(vehicle) {
  if (!vehicle || vehicle.lastRefreshMs == null) return false;
  return Date.now() - Number(vehicle.lastRefreshMs) > LIVE_DATA_MAX_AGE_MS;
}

/**
 * Fetch live vehicles data from the vehicles API
 * Accepts either route name (routetext) or route ID
 * @param {string} apiPath - The API path from city config
 * @param {string|number} routeIdentifier - The route name (e.g., "KIA-14") or route ID
 * @param {number} serviceTypeId - The service type ID (default: 0 for all types)
 * @returns {Promise} Promise resolving to vehicle data with a vehicles array
 *   and a `source` naming what the endpoint derived positions from
 *   ('api' or 'gtfs-rt'), or null when no live source backs the route
 */
export function fetchVehicles(
  apiPath,
  routeIdentifier,
  serviceTypeId = 0,
) {
  if (!apiPath || !routeIdentifier || isApiDisabled()) {
    return Promise.resolve(null);
  }

  // Determine if routeIdentifier is a number (route ID) or string (route name)
  const isRouteId = typeof routeIdentifier === 'number';
  const paramName = isRouteId ? 'routeid' : 'routetext';
  const paramValue = encodeURIComponent(routeIdentifier);
  const url = `${getApiUrl(apiPath)}?${paramName}=${paramValue}&servicetypeid=${serviceTypeId}`;

  const cached = recentRequests.get(url);
  if (
    cached &&
    (cached.settledAt === null || Date.now() - cached.settledAt < REQUEST_DEDUPE_MS)
  ) {
    return cached.promise;
  }

  const promise = (async () => {
    try {
      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`Failed to fetch vehicles: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching vehicles:', error);
      return null;
    }
  })();
  const entry = { promise, settledAt: null };
  recentRequests.set(url, entry);
  promise.finally(() => {
    entry.settledAt = Date.now();
    setTimeout(() => {
      if (recentRequests.get(url) === entry) recentRequests.delete(url);
    }, REQUEST_DEDUPE_MS);
  });
  return promise;
}

/**
 * Reconstruct vehicle location from GeoJSON feature
 * Location is stored in GeoJSON coordinates to reduce redundancy
 * @param {Object} geoJSON - GeoJSON FeatureCollection
 * @param {string} vehicleId - Vehicle ID to look up
 * @returns {Object|null} Location object with lat and lng, or null if not found
 */
export function getVehicleLocationFromGeoJSON(geoJSON, vehicleId) {
  if (!geoJSON || !geoJSON.features || !vehicleId) {
    return null;
  }

  const feature = geoJSON.features.find(
    (f) => f.id === vehicleId || f.properties?.vehicleId === vehicleId,
  );

  if (!feature || !feature.geometry || !feature.geometry.coordinates) {
    return null;
  }

  // GeoJSON coordinates are [longitude, latitude]
  const [lng, lat] = feature.geometry.coordinates;
  return { lat, lng };
}

/**
 * Enrich vehicles array with location data from GeoJSON
 * Useful when location is needed but was removed to reduce redundancy
 * @param {Array} vehicles - Array of vehicle objects (without location)
 * @param {Object} geoJSON - GeoJSON FeatureCollection with vehicle locations
 * @returns {Array} Vehicles array with location added
 */
export function enrichVehiclesWithLocation(vehicles, geoJSON) {
  if (!vehicles || !geoJSON) {
    return vehicles || [];
  }

  return vehicles.map((vehicle) => {
    const location = getVehicleLocationFromGeoJSON(geoJSON, vehicle.vehicleId);
    return location ? { ...vehicle, location } : vehicle;
  });
}

/**
 * Convert vehicles to GeoJSON features for map display
 * Only includes minimal properties needed for map rendering
 * Full vehicle data is available in the vehicles array, linked by vehicleId
 * @param {Array} vehicles - Array of vehicle objects with location
 * @returns {Object} GeoJSON FeatureCollection
 */
export function vehiclesToGeoJSON(vehicles) {
  if (!vehicles || vehicles.length === 0) {
    return {
      type: 'FeatureCollection',
      features: [],
    };
  }

  const features = vehicles
    .filter((vehicle) => {
      // Exclude vehicles with lastRefreshMs older than 15 minutes
      if (isVehicleStale(vehicle)) return false;
      // Only include vehicles with valid location data
      if (
        !vehicle.location ||
        typeof vehicle.location.lat !== 'number' ||
        typeof vehicle.location.lng !== 'number'
      ) {
        return false;
      }
      // Filter out invalid coordinates (0,0 or null island)
      if (vehicle.location.lat === 0 && vehicle.location.lng === 0) {
        return false;
      }
      return true;
    })
    .map((vehicle, index) => ({
      type: 'Feature',
      id: vehicle.vehicleId || `vehicle-${index}`,
      properties: {
        vehicleId: vehicle.vehicleId,
        vehicleNumber: vehicle.vehicleNumber, // Needed for text label on map
        heading: vehicle.heading, // Needed for icon rotation
      },
      geometry: {
        type: 'Point',
        coordinates: [vehicle.location.lng, vehicle.location.lat],
      },
    }));

  return {
    type: 'FeatureCollection',
    features,
  };
}

/**
 * Create a vehicle tracker instance for managing live vehicles
 * Supports tracking multiple services simultaneously
 * @param {Object} config - Configuration object
 * @param {Object} config.cityConfig - City configuration with API paths
 * @param {Object} config.map - Maplibre GL map instance
 * @param {Function} config.setRafInterval - RAF interval setter function
 * @param {Function} config.clearRafInterval - RAF interval clearer function
 * @returns {Object} Vehicle tracker instance
 */
export function createVehicleTracker({
  cityConfig,
  map,
  setRafInterval,
  clearRafInterval,
}) {
  let intervalId = null;
  const trackedServices = new Set(); // Track multiple services
  const currentVehicles = new Map(); // Map service number to vehicles array
  const routeIdCache = new Map(); // Cache route IDs: service number -> route ID
  const subscribers = new Set(); // Subscribers for vehicle updates
  const statusSubscribers = new Set(); // Subscribers for live data status
  let liveStatus = IDLE_STATUS;
  let activeServicesKey = '';
  let generation = 0;
  let startPromise = null;
  let hasRenderedVehicles = false;

  function setLiveStatus(next) {
    if (
      liveStatus.loading === next.loading &&
      liveStatus.error === next.error &&
      liveStatus.source === next.source
    ) {
      return;
    }
    liveStatus = next;
    statusSubscribers.forEach((callback) => callback(liveStatus));
  }

  /**
   * Update vehicles for all tracked services
   */
  async function updatePositions(expectedGeneration = generation) {
    const vehicleTracking = cityConfig?.liveVehicles;

    // Early return if vehicle tracking is disabled or no services are tracked
    if (!vehicleTracking?.enabled || trackedServices.size === 0) {
      return;
    }

    try {
      // Fetch vehicles for all tracked services in parallel
      const fetchPromises = Array.from(trackedServices).map(
        async (serviceNumber) => {
          try {
            // Check cache for route ID first
            const cachedRouteId = routeIdCache.get(serviceNumber);
            const routeIdentifier = cachedRouteId || serviceNumber;

            const response = await fetchVehicles(
              vehicleTracking.apiPath,
              routeIdentifier,
            );

            // Cache the route ID from the response if available
            if (response && response.routeId) {
              routeIdCache.set(serviceNumber, response.routeId);
            }

            return { serviceNumber, response };
          } catch (error) {
            console.error(
              `Error fetching vehicles for service ${serviceNumber}:`,
              error,
            );
            return { serviceNumber, response: null };
          }
        },
      );

      const results = await Promise.all(fetchPromises);
      if (expectedGeneration !== generation) return;

      // Status requires an actual vehicle, not just a responding endpoint —
      // a working feed that currently has nothing to show still reads as
      // "no live data" to the rider, same as the feed being down.
      const liveSource =
        results.find(({ response }) => response?.source && response.vehicles?.length)
          ?.response?.source ?? null;
      setLiveStatus({ loading: false, error: !liveSource, source: liveSource });

      // Combine all vehicles from all services
      const allVehicles = [];

      results.forEach(({ serviceNumber, response }) => {
        if (!response?.vehicles) return;
        const freshVehicles = response.vehicles.filter(
          (vehicle) => !isVehicleStale(vehicle),
        );
        currentVehicles.set(serviceNumber, freshVehicles);
        allVehicles.push(...freshVehicles);
      });

      // Generate GeoJSON from vehicles on the client side
      const combinedGeoJSON = vehiclesToGeoJSON(allVehicles);

      // Update map if source exists
      const source = map?.getSource('buses-service');
      const hasVehicles = combinedGeoJSON.features.length > 0;
      if (source && (hasVehicles || hasRenderedVehicles)) {
        source.setData(combinedGeoJSON);
      }
      hasRenderedVehicles = hasVehicles;

      // Notify subscribers
      subscribers.forEach((callback) => {
        callback(allVehicles);
      });
    } catch (error) {
      console.error('Error updating vehicle positions:', error);
      setLiveStatus({ loading: false, error: true, source: null });
      subscribers.forEach((callback) => callback([]));
    }
  }

  /**
   * Start tracking vehicles for multiple services
   * @param {Array<string>} serviceNumbers - Array of service numbers to track
   * @returns {Promise<boolean>} True if tracking started successfully
   */
  function startServices(serviceNumbers) {
    if (!serviceNumbers || serviceNumbers.length === 0) {
      stop();
      return Promise.resolve(false);
    }

    const vehicleTracking = cityConfig?.liveVehicles;

    if (!vehicleTracking?.enabled) {
      console.log('Vehicle tracking not enabled');
      return Promise.resolve(false);
    }

    const normalizedServices = [...new Set(serviceNumbers.map(String))];
    const servicesKey = normalizedServices.join('|');
    if (servicesKey === activeServicesKey) {
      if (startPromise) return startPromise;
      if (intervalId) return Promise.resolve(true);
    }

    const currentGeneration = ++generation;
    activeServicesKey = servicesKey;
    setLiveStatus({ loading: true, error: false, source: null });
    startPromise = (async () => {
      // Stop existing tracking
      if (intervalId) {
        clearRafInterval(intervalId);
        intervalId = null;
      }

      // Clear existing tracked services
      trackedServices.clear();
      currentVehicles.clear();

      // Add services to tracking
      normalizedServices.forEach((serviceNumber) => {
        trackedServices.add(serviceNumber);
      });

      if (trackedServices.size === 0) {
        console.warn('No valid services to track');
        stop();
        return false;
      }

      // Fetch vehicles immediately for all services
      await updatePositions(currentGeneration);
      if (currentGeneration !== generation) return false;

      // Only set up periodic updates if we have tracked services
      if (trackedServices.size > 0) {
        intervalId = setRafInterval(() => {
          updatePositions(currentGeneration);
        }, 60 * 1000);
      }

      return true;
    })()
      .catch((error) => {
        console.error('Error starting vehicle tracking:', error);
        return false;
      })
      .finally(() => {
        if (currentGeneration === generation) startPromise = null;
      });
    return startPromise;
  }

  /**
   * Start tracking vehicles for a single service (backward compatibility)
   * @param {string} serviceNumber - The service number to track
   * @returns {Promise<boolean>} True if tracking started successfully
   */
  async function start(serviceNumber) {
    return startServices([serviceNumber]);
  }

  /**
   * Stop tracking vehicles and clear map
   */
  function stop() {
    generation++;
    activeServicesKey = '';
    startPromise = null;
    setLiveStatus(IDLE_STATUS);
    if (intervalId) {
      clearRafInterval(intervalId);
      intervalId = null;
    }

    const hadVehicles = getVehicles().length > 0;
    trackedServices.clear();
    currentVehicles.clear();

    // Clear vehicles from map
    const source = map?.getSource('buses-service');
    if (source && hasRenderedVehicles) {
      source.setData({
        type: 'FeatureCollection',
        features: [],
      });
    }
    hasRenderedVehicles = false;

    if (hadVehicles) subscribers.forEach((callback) => callback([]));
  }

  /**
   * Get current tracking status
   * @returns {Object} Current tracking status
   */
  function getStatus() {
    return {
      isTracking: intervalId !== null,
      serviceNumbers: Array.from(trackedServices),
    };
  }

  /**
   * Get current vehicles (all tracked services combined)
   * @returns {Array} Current vehicles array
   */
  function getVehicles() {
    const allVehicles = [];
    currentVehicles.forEach((vehicles) => {
      allVehicles.push(...vehicles);
    });
    return allVehicles;
  }

  /**
   * Subscribe to vehicle updates
   * @param {Function} callback - Callback function to receive vehicle updates
   * @returns {Function} Unsubscribe function
   */
  function subscribe(callback) {
    subscribers.add(callback);
    // Immediately call with current vehicles
    const current = getVehicles();
    if (current.length > 0) {
      callback(current);
    }
    return () => subscribers.delete(callback);
  }

  /**
   * Subscribe to live data status updates ({ loading, error, source })
   * @param {Function} callback - Callback function to receive status updates
   * @returns {Function} Unsubscribe function
   */
  function subscribeLiveStatus(callback) {
    statusSubscribers.add(callback);
    callback(liveStatus);
    return () => statusSubscribers.delete(callback);
  }

  return {
    start,
    startServices, // New method for multiple services
    stop,
    getStatus,
    getVehicles,
    subscribe,
    subscribeLiveStatus,
  };
}
